import { useEffect, useMemo, useState } from 'react';
import Button from 'epn-ui/umd/dist/components/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldCheck } from '@fortawesome/pro-regular-svg-icons/faShieldCheck';
import { initEpnBaseMetric } from 'src/metric/EpnBaseMetric';
import { initCryptoMetric } from 'src/metric/CryptoMetric';

import useI18n from '@i18n';
import { METRIC_ENUM } from 'src/metric/config';

import {
    clearJivoStorage,
    CookieConsentValue,
    getCookieConsent,
    isCookieConsentEnabled,
    loadJivoScript,
    setCookieConsent,
} from './utils';
import BaseSemanticLink from '../BaseSemanticLink';

const oldCabinetLinks = JSON.parse(process.env.NEXT_PUBLIC_OLD_CABINET_LINKS);

interface ICookieConsentProps {
    isAuth: boolean | null;
    metricType?: METRIC_ENUM;
}

const BaseCookiesConsent: React.FC<ICookieConsentProps> = ({ isAuth, metricType }) => {
    const { t } = useI18n('Base');
    const [consent, setConsent] = useState<CookieConsentValue | null | undefined>(undefined);

    const shouldRender = useMemo(() => isCookieConsentEnabled(), []);
    const showLiveChat = shouldRender && isAuth && process.env.NEXT_PUBLIC_PROJECT_NAME === 'epn';

    useEffect(() => {
        if (!shouldRender) return;

        setConsent(getCookieConsent());
    }, [shouldRender]);

    useEffect(() => {
        if (!shouldRender || !showLiveChat) return undefined;

        if (consent !== CookieConsentValue.all) {
            clearJivoStorage();
            window.addEventListener('beforeunload', clearJivoStorage);
        }

        loadJivoScript();

        return () => {
            window.removeEventListener('beforeunload', clearJivoStorage);
        };
    }, [consent, shouldRender, showLiveChat]);

    useEffect(() => {
        if (!shouldRender) return;
        if (consent !== CookieConsentValue.all) return;

        if (metricType === METRIC_ENUM.cryptoMetric) {
            initCryptoMetric();
            return;
        }

        initEpnBaseMetric();
    }, [consent, metricType, shouldRender]);

    const onAcceptAll = (): void => {
        setCookieConsent(CookieConsentValue.all);
        setConsent(CookieConsentValue.all);
    };

    const onAcceptNecessary = (): void => {
        setCookieConsent(CookieConsentValue.necessary);
        setConsent(CookieConsentValue.necessary);
    };

    if (!shouldRender || consent !== null) return null;

    return (
        <div className="fixed bottom-0 left-1/2 z-[1200] w-[calc(100%-16px)] -translate-x-1/2 sm:bottom-6 sm:w-full sm:max-w-[568px]">
            <div className="relative w-full px-2 py-3 bg-white rounded-[6px] shadow-1 sm:p-5">
                <div className="flex items-center mb-3 gap-2">
                    <FontAwesomeIcon
                        icon={faShieldCheck}
                        className="hidden text-green-500 sm:block text-sub"
                    />
                    <p className="pr-2 font-medium text-p1 sm:text-sub text-blueGray-800">
                        {t('We use Cookies')}
                    </p>
                </div>
                <p className="text-p2 text-blueGray-500">
                    {t('To improve the website, we use Yandex.Metrica analytics')}{' '}
                    <BaseSemanticLink
                        className="underline text-blueGray-600"
                        to={oldCabinetLinks.privacy}
                        target="_blank"
                        rel="noreferrer"
                    >
                        {t('Learn more')}
                    </BaseSemanticLink>
                    .
                </p>
                <div className="flex flex-col mt-5 gap-2.5 sm:flex-row">
                    <Button className="w-full" type="primary" onClick={onAcceptAll}>
                        {t('Accept all')}
                    </Button>
                    <Button className="w-full" type="default" onClick={onAcceptNecessary}>
                        {t('Necessary only')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default BaseCookiesConsent;
